import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

interface DeckEmailGateProps {
  onSubmit: (email: string, viewId: string) => void;
  deckType?: string;
}

const DeckEmailGate = ({ onSubmit, deckType = "sales" }: DeckEmailGateProps) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const trimmed = email.trim();
    if (!trimmed) {
      setError("please enter your email address.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("please enter a valid email address.");
      return;
    }

    setLoading(true);
    let viewId = "";
    try {
      const { data, error: dbError } = await supabase
        .from("deck_views")
        .insert({
          email: trimmed,
          user_agent: navigator.userAgent,
          deck_type: deckType,
        })
        .select("id")
        .single();

      if (dbError) throw dbError;
      viewId = data.id;
    } catch {
      // If DB fails, still let them view
    } finally {
      setLoading(false);
    }

    // Trigger fade-out, then call onSubmit after animation
    setClosing(true);
    setTimeout(() => {
      onSubmit(trimmed, viewId);
    }, 400);
  };

  return (
    <AnimatePresence>
      {!closing && (
        <motion.div
          key="gate-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
        >
          <motion.div
            key="gate-card"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.97 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="bg-background rounded-3xl p-8 laptop:p-10 max-w-md w-full shadow-2xl border border-foreground/10"
          >
            {/* qwip branding */}
            <h2 className="font-funnel font-bold text-3xl text-foreground mb-2 text-center">
              qwip
            </h2>
            <p className="font-funnel text-lg text-foreground text-center mb-8">
              requires your email to continue
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block font-sans font-semibold text-sm text-foreground mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full px-4 py-3 rounded-xl border-2 border-foreground/15 bg-background text-foreground font-sans text-base focus:outline-none focus:border-primary transition-colors"
                  autoFocus
                  disabled={loading}
                />
                {error && (
                  <p className="text-red-500 text-xs font-sans mt-1.5">{error}</p>
                )}
              </div>

              <p className="text-xs text-muted-foreground font-sans leading-relaxed">
                This information will be shared with qwip ltd. Learn more about how we use and protect your data in our{" "}
                <a
                  href="https://qwip.co.uk/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline hover:opacity-80"
                >
                  Privacy Policy
                </a>
                .
              </p>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-foreground text-primary-foreground font-sans font-semibold text-base py-3.5 rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {loading ? "..." : "continue"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DeckEmailGate;
